import type { ChangeEvent } from 'react';
import { Input } from './ui/Input';
import { Label } from './ui/Label';

interface CampoDef {
  id: string;
  nome: string;
  label: string;
  tipo: string;
  obrigatorio: boolean;
  ajuda?: string;
  placeholder?: string;
  opcoes?: Array<{ valor: string; label: string }>;
}

interface Props {
  campos: CampoDef[];
  valores: Record<string, unknown>;
  onChange: (valores: Record<string, unknown>) => void;
  erros?: Record<string, string>;
}

export function CamposCustomizadosRenderer({ campos, valores, onChange, erros }: Props) {
  function set(nome: string, valor: unknown) {
    onChange({ ...valores, [nome]: valor });
  }

  if (campos.length === 0) return null;

  return (
    <div className="space-y-4">
      {campos.map((c) => (
        <Campo
          key={c.id}
          campo={c}
          valor={valores[c.nome]}
          onChange={(v) => set(c.nome, v)}
          erro={erros?.[c.nome]}
        />
      ))}
    </div>
  );
}

function Campo({
  campo, valor, onChange, erro,
}: {
  campo: CampoDef;
  valor: unknown;
  onChange: (v: unknown) => void;
  erro?: string;
}) {
  const tipo = campo.tipo;

  if (tipo === 'texto_longo') {
    return (
      <Wrapper campo={campo} erro={erro}>
        <textarea
          value={(valor as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          required={campo.obrigatorio}
          placeholder={campo.placeholder}
          rows={4}
          className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional resize-y"
        />
      </Wrapper>
    );
  }

  if (tipo === 'selecao') {
    return (
      <Wrapper campo={campo} erro={erro}>
        <select
          value={(valor as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          required={campo.obrigatorio}
          className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional"
        >
          <option value="">{campo.placeholder ?? '— Selecione —'}</option>
          {(campo.opcoes ?? []).map((o) => (
            <option key={o.valor} value={o.valor}>{o.label}</option>
          ))}
        </select>
      </Wrapper>
    );
  }

  if (tipo === 'multi_selecao') {
    const selecionados = Array.isArray(valor) ? (valor as string[]) : [];
    function toggle(v: string) {
      onChange(selecionados.includes(v) ? selecionados.filter((s) => s !== v) : [...selecionados, v]);
    }
    return (
      <Wrapper campo={campo} erro={erro}>
        <div className="flex flex-wrap gap-x-4 gap-y-2 border border-slate-200 rounded p-3 bg-slate-50/50">
          {(campo.opcoes ?? []).map((o) => (
            <label key={o.valor} className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={selecionados.includes(o.valor)}
                onChange={() => toggle(o.valor)}
                className="rounded text-institutional focus:ring-institutional"
              />
              <span className="text-slate-700">{o.label}</span>
            </label>
          ))}
        </div>
      </Wrapper>
    );
  }

  if (tipo === 'booleano') {
    return (
      <Wrapper campo={campo} erro={erro}>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={!!valor}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded text-institutional focus:ring-institutional"
          />
          <span className="text-slate-700">{campo.placeholder ?? 'Sim'}</span>
        </label>
      </Wrapper>
    );
  }

  // Mapeia tipo para input type
  let inputType = 'text';
  let inputMode: 'text' | 'numeric' | 'decimal' | 'email' | 'tel' | undefined;
  let onChangeValor: (v: ChangeEvent<HTMLInputElement>) => void = (e) => onChange(e.target.value);

  if (tipo === 'data') inputType = 'date';
  else if (tipo === 'datahora') inputType = 'datetime-local';
  else if (tipo === 'email') { inputType = 'email'; inputMode = 'email'; }
  else if (tipo === 'numero') { inputType = 'number'; inputMode = 'numeric'; onChangeValor = (e) => onChange(e.target.value === '' ? null : parseInt(e.target.value, 10)); }
  else if (tipo === 'decimal') { inputType = 'number'; inputMode = 'decimal'; onChangeValor = (e) => onChange(e.target.value === '' ? null : parseFloat(e.target.value)); }
  else if (tipo === 'cep') inputMode = 'numeric';
  else if (tipo === 'cpf' || tipo === 'cnpj') inputMode = 'numeric';
  else if (tipo === 'telefone') { inputType = 'tel'; inputMode = 'tel'; }

  return (
    <Wrapper campo={campo} erro={erro}>
      <Input
        type={inputType}
        value={valor === null || valor === undefined ? '' : String(valor)}
        onChange={onChangeValor}
        required={campo.obrigatorio}
        placeholder={campo.placeholder}
        inputMode={inputMode}
        invalido={!!erro}
      />
    </Wrapper>
  );
}

function Wrapper({ campo, erro, children }: { campo: CampoDef; erro?: string; children: React.ReactNode }) {
  return (
    <div>
      <Label obrigatorio={campo.obrigatorio}>{campo.label}</Label>
      {children}
      {campo.ajuda && !erro && <p className="text-xs text-slate-500 mt-1">{campo.ajuda}</p>}
      {erro && <p className="text-xs text-red-600 mt-1">{erro}</p>}
    </div>
  );
}
